<template>
    <div class="EntityDetailsStatusBar">
        <div v-if="entity.active" class="EntityDetailsStatusBar__statusContainer">
            <ComplianceStatusBadge
                class="EntityDetailsStatusBar__headerStatusBadge"
                :status="entity.complianceStatus"
            />
            <VerificationStatusBadge
                v-if="entity.paused"
                class="EntityDetailsStatusBar__headerVerificationStatusBadge"
            >
                Paused
            </VerificationStatusBadge>
            <VerificationStatusBadge
                v-else
                class="EntityDetailsStatusBar__headerVerificationStatusBadge"
                :status="entity.verificationStatus"
            />
        </div>
        <div v-else>
            <Badge class="EntityDetailsStatusBar__headerDeactivatedBadge">
                Deactivated
            </Badge>
        </div>
        <ExceptionBadge
            v-if="entityException"
            class="EntityDetailsStatusBar__exceptionBadge"
            label="Exception Granted"
            :exception-type="entityException.type"
            :until="entityException.until"
            applied-to="Entity"
            :note="entityException.note"
        />
        <RbacDisplayGuard
            :features="[RbacFeature.dashboardPauseEntity,
                        RbacFeature.dashboardActivateDeactivateEntity,
                        RbacFeature.entityDetailsAddRemoveException,
                        RbacFeature.dashboardCreateNewLink,
                        RbacFeature.dashboardCopySubmissionLink,
                        RbacFeature.dashboardViewPendingActions,
                        RbacFeature.dashboardViewSubmissionHistory,
                        RbacFeature.dashboardUploadEntityDocument,
                        RbacFeature.entityNoteCreate]"
            required-any
        >
            <ActionsButton
                :loading="updatingEntity"
                :entity="entity"
                :exception-override="entityException"
                :is-loading-submission-link="isLoadingSubmissionLink"
                :is-collateral-enabled="isCollateralEnabled"
                :submission-link="getSubmissionLink(entity.id)"
                @actions-button-clicked="$emit('loadEntityRequests', entity.id)"
                @grant-exception-to-entity="grantException"
                @remove-exceptions="removeExceptions"
                @set-entities-pause-state="$emit('setEntitiesPauseState', $event)"
                @set-entities-active-state="$emit('setEntitiesActiveState', $event)"
                @send-document-request="$emit('sendDocumentRequest', $event)"
                @copy-submission-link="$emit('copySubmissionLink', $event)"
                @open-collateral-mapping-modal="$emit('open-collateral-mapping-modal')"
                @view-submission-history="$emit('view-submission-history')"
                @upload-entity-document="$emit('upload-entity-document')"
                @add-note="$emit('add-note', $event)"
            />
        </RbacDisplayGuard>
    </div>
</template>
<script lang="ts">
    import { Component, Prop, Vue } from '@evidentid/vue-property-decorator';

    import { Badge } from '@evidentid/dashboard-commons/components/Badge';
    import RbacDisplayGuard
        from '@evidentid/dashboard-commons/modules/role-base-access-control/components/rbac-guard/RbacDisplayGuard.vue';
    import { Entity } from '@evidentid/tprm-portal-lib/models/dashboard';
    import { ComplianceStatusOverride } from '@evidentid/tprm-portal-lib/models/entity-details';
    import { RbacFeature } from '@evidentid/tprm-portal-lib/models/rbac';

    import ActionsButton from '@/modules/dashboard/components/ActionsButton/ActionsButton.vue';
    import ComplianceStatusBadge from '@/modules/dashboard/components/ComplianceStatusBadge/ComplianceStatusBadge.vue';
    import VerificationStatusBadge
        from '@/modules/dashboard/components/VerificationStatusBadge/VerificationStatusBadge.vue';
    import ExceptionBadge from '@/modules/entity-details/components/ExceptionBadge/ExceptionBadge.vue';
    import { createEntityLevelException } from '@/modules/entity-details/utils/createException';

    @Component({
        emits: [ 'add-note', 'copySubmissionLink', 'grantExceptions', 'loadEntityRequests', 'open-collateral-mapping-modal', 'removeExceptions', 'sendDocumentRequest', 'setEntitiesActiveState', 'setEntitiesPauseState', 'upload-entity-document', 'view-submission-history' ],
        components: {
            RbacDisplayGuard,
            ActionsButton,
            Badge,
            ComplianceStatusBadge,
            ExceptionBadge,
            VerificationStatusBadge,
        },
    })
    export default class EntityDetailsStatusBar extends Vue {
        private RbacFeature = RbacFeature;

        @Prop({ type: Object })
        private entity!: Entity;

        @Prop({ type: Boolean, default: false })
        private updatingEntity!: boolean;

        @Prop({ type: Boolean, default: false })
        private isLoadingSubmissionLink!: boolean;

        @Prop({ type: Object, default: () => ({}) })
        private submissionLinkPerEntityId!: Record<string, string>;

        @Prop({ type: Object, default: null })
        private entityException!: ComplianceStatusOverride;

        @Prop({ type: Boolean, default: false })
        private isCollateralEnabled!: boolean;

        private getSubmissionLink(entItyId: string): string {
            return this.submissionLinkPerEntityId[entItyId] || '';
        }

        private grantException() {
            this.$emit('grantExceptions', [ createEntityLevelException() ]);
        }

        private removeExceptions(exception: string[]) {
            this.$emit('removeExceptions', exception);
        }
    }
</script>
